import {
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { NewAvatarRequest } from 'types/avatar-system/avatar-system';
import { useAddAvatarMutation } from 'store/slices/cards/avatar/avatar';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';
import ViewProduct from 'component/view-product';
import PageviewIcon from '@mui/icons-material/Pageview';

interface Props {
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
    avatarCategory?: string[];
}

const AddNewAvatar: React.FC<Props> = (props: Props) => {
    const { setOpenDialog, refetch, avatarCategory } = props;
    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] =
        React.useState(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<NewAvatarRequest>({
        defaultValues: {
            avatarName: undefined,
            avatarType: undefined,
            avatarCategory: undefined,
            productId: undefined,
        },
    });

    const [addAvatar, { error, isError, isSuccess, isLoading }] =
        useAddAvatarMutation();

    const { data: dataProductToonTint, isFetching } =
        useProductTitleSearchListQuery({
            productType: 'avatar',
            searchText: searchProduct,
        });

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const onSubmit = (values: NewAvatarRequest) => {
        addAvatar(values);
    };
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    ADD NEW AVATAR
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                <Box
                    ref={formRef}
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box
                        pt={pxToRem(15)}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Controller
                            name="avatarName"
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    label="Avatar Name"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                />
                            )}
                        />
                        <FormControl
                            error={Boolean(errors?.avatarCategory)}
                            fullWidth
                            sx={{ ml: pxToRem(10) }}
                        >
                            <InputLabel>Avatar Category</InputLabel>
                            <Controller
                                name="avatarCategory"
                                control={control}
                                rules={{
                                    required:
                                        'Avatar Category Type is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Avatar Categori"
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                    >
                                        {avatarCategory?.map((status) => (
                                            <MenuItem
                                                key={status}
                                                value={status}
                                            >
                                                {status}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.avatarCategory && (
                                <FormHelperText>
                                    {errors?.avatarCategory.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    <Box
                        mt={pxToRem(10)}
                        display={'flex'}
                        alignItems={'center'}
                    >
                        <Controller
                            name="productId"
                            control={control}
                            render={({ field: { onChange, ref, value } }) => (
                                <Autocomplete
                                    limitTags={5}
                                    disableCloseOnSelect
                                    sx={{
                                        width: viewProduct ? '95%' : '100%',
                                    }}
                                    options={dataProductToonTint ?? []}
                                    loading={isFetching}
                                    value={
                                        (dataProductToonTint ?? []).find(
                                            (opt) => opt.id === value,
                                        ) ?? null
                                    }
                                    onChange={(_, newValue) => {
                                        onChange(newValue ? newValue.id : '');
                                        setViewProduct(
                                            newValue ? newValue.id : '',
                                        );
                                    }}
                                    inputValue={searchProduct}
                                    onInputChange={(_, newInput, reason) => {
                                        if (reason !== 'reset') {
                                            setSearchProduct(newInput);
                                        }
                                    }}
                                    getOptionLabel={(option) =>
                                        option.title || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Product"
                                            variant="outlined"
                                            fullWidth
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {isFetching && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            )}
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                        {viewProduct && (
                            <Box pl={pxToRem(10)} sx={{ cursor: 'pointer' }}>
                                <Tooltip title="View Product">
                                    <PageviewIcon
                                        fontSize={'large'}
                                        onClick={() => {
                                            setOpenViewProductDialog(true);
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Create Avatar
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '700px',
                            minHeight: '550px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Avatar!'}
            />
        </>
    );
};
export default AddNewAvatar;
